import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Zap, Grid3x3, Palette, Bell, Trophy, Camera, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { isNativeApp } from "@/lib/platform";
import { formatCurrency } from "@/lib/utils";
import { APPLE_PRICING_USD, WEB_PRICING_USD } from "@/lib/purchases";

interface SubscriptionGateProps {
  open: boolean;
}

export const SubscriptionGate = ({ open }: SubscriptionGateProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const native = isNativeApp();

  const monthlyPrice = native ? APPLE_PRICING_USD.MONTHLY : WEB_PRICING_USD.MONTHLY;
  const annualPrice = native ? APPLE_PRICING_USD.ANNUAL : WEB_PRICING_USD.ANNUAL;
  const lifetimePrice = native ? APPLE_PRICING_USD.LIFETIME : WEB_PRICING_USD.LIFETIME;
  const annualMonthly = annualPrice / 12;

  const features = [
    { name: t('upgrade.features.unlimited_ai'), icon: Zap },
    { name: t('upgrade.features.custom_metrics'), icon: Grid3x3 },
    { name: t('upgrade.features.progress_snaps'), icon: Camera },
    { name: t('upgrade.features.chart_colors'), icon: Palette },
    { name: t('upgrade.features.reminders'), icon: Bell },
    { name: t('upgrade.features.leaderboard'), icon: Trophy },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-background overflow-y-auto"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
        >
          {/* Background gradient effect - dark mode only */}
          <div className="dark:block hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 2, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 pointer-events-none overflow-hidden will-change-[opacity]"
              style={{ top: '15%' }}
            >
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] h-[85%] bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-purple-600/20 via-blue-500/10 to-transparent blur-[100px]" />
            </motion.div>
          </div>

          <div className="min-h-screen p-4 flex flex-col">
            <div className="max-w-md mx-auto w-full flex-1 flex flex-col pt-14 relative z-10">
              {/* Header */}
              <div className="text-center space-y-2 pb-5">
                <h1 className="text-2xl font-bold">{t('paywall.gate_title')}</h1>
                <p className="text-sm text-muted-foreground">{t('paywall.gate_subtitle')}</p>
              </div>

              {/* Included features */}
              <div className="space-y-2 pb-5">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 px-2">
                    <div className="flex-shrink-0 p-1.5 rounded-full bg-accent/10">
                      <feature.icon className="h-4 w-4 text-accent" />
                    </div>
                    <span className="text-sm font-medium">{feature.name}</span>
                    <Check className="h-4 w-4 text-accent ml-auto flex-shrink-0" />
                  </div>
                ))}
              </div>

              {/* Pricing cards */}
              <div className="space-y-3 pb-4">
                <div className="grid grid-cols-2 gap-3">
                  {/* Monthly */}
                  <Card className="p-3 border-2 border-accent/40 !bg-transparent">
                    <div className="space-y-1.5">
                      <h3 className="font-semibold text-sm">{t('upgrade.plans.monthly')}</h3>
                      <div className="text-lg font-bold">
                        {formatCurrency(monthlyPrice)}
                        <span className="text-[10px] text-muted-foreground">{t('upgrade.plans.per_month')}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('upgrade.plans.trial_days')}</p>
                    </div>
                  </Card>

                  {/* Annual */}
                  <Card className="p-3 border-2 border-accent relative !bg-transparent">
                    <div className="absolute -top-2 right-2 bg-green-600 px-2 py-0.5 rounded-full">
                      <span className="text-white text-xs font-bold">{native ? t('upgrade.plans.discount_51') : t('upgrade.plans.discount_58')}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-sm">{t('upgrade.plans.annual')}</h3>
                        <span className="text-[10px] text-muted-foreground">({formatCurrency(annualMonthly)}{t('upgrade.plans.per_month')})</span>
                      </div>
                      <div className="text-lg font-bold">
                        {formatCurrency(annualPrice)}
                        <span className="text-[10px] text-muted-foreground">{t('upgrade.plans.per_year')}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('upgrade.plans.trial_days')}</p>
                    </div>
                  </Card>
                </div>

                {/* Lifetime */}
                <Card className="p-3 border-2 border-purple-500 relative !bg-transparent">
                  <div className="absolute -top-2 right-2 bg-purple-600 px-2 py-0.5 rounded-full">
                    <span className="text-white text-xs font-bold">{t('upgrade.plans.best_value')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-sm">{t('upgrade.plans.lifetime')}</h3>
                      <div className="text-lg font-bold">
                        {formatCurrency(lifetimePrice)}
                        <span className="text-[10px] text-muted-foreground"> {t('upgrade.plans.once')}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('upgrade.plans.forever_access')}</p>
                  </div>
                </Card>
              </div>

              {/* CTA */}
              <Button
                onClick={() => navigate("/upgrade")}
                className="w-full bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70"
                size="lg"
              >
                {t('paywall.subscribe_cta')}
              </Button>

              <p className="text-xs text-center text-muted-foreground pt-2">
                {t('paywall.no_charge_note')}
              </p>

              {/* Indie support bubble */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="pt-4"
              >
                <Card className="p-4 border border-pink-500/30 bg-gradient-to-r from-pink-500/5 to-purple-500/5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-pink-500/10 rounded-full">
                      <Heart className="h-5 w-5 text-pink-500 fill-pink-500" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">{t('upgrade.indie_support.title')}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t('upgrade.indie_support.description')}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>

              <p className="text-xs text-center text-muted-foreground pt-3 pb-8">
                {t('upgrade.footer.cancel_anytime')}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
